import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, Redirect } from 'react-router';
import { useTranslation } from 'react-i18next';
import { usersActions } from '../redux/actions';
import OrganizationCommunity from '../components/OrganizationCommunity';
import { CardLoader } from '../components/Loader';
import useError from '../utils/HookErrorUtil';
import api from '../api';
import { CAN_MANAGE_PUBLIC_PROFILE } from '../constants';

/**
 * Page for admin team members to manage an organization's public profile.
 * This page is accessible to users who have the CAN_MANAGE_PUBLIC_PROFILE permission
 * for an organization through their admin team membership.
 */
function ManagedOrganizationProfile(props) {
  const { match } = props;
  const { orgId } = match.params;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { handleCatched } = useError();

  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);

  const authUser = useSelector((state) => state.auth.authUser);
  // Use Redux state for organization to get updates after patches
  const organization = useSelector((state) => state.users[Number(orgId)]);

  const fetchUser = async (id) => dispatch(usersActions.read(id, {}));

  useEffect(() => {
    const checkPermissionAndFetchOrg = async () => {
      try {
        // Fetch admin team memberships to check permission
        const memberships = await api.list(
          'admin-team-members',
          { user: authUser.id },
          { pagination: 'no' },
        );

        // Check if user has CAN_MANAGE_PUBLIC_PROFILE permission for this org
        const hasAccess = memberships.some((membership) => {
          const teamOwnerId = membership.team_details?.owner;
          if (teamOwnerId !== Number(orgId)) return false;

          const permissions = membership.team_permissions || [];
          const permissionNames = permissions.map((p) => p.name);
          return permissionNames.includes(CAN_MANAGE_PUBLIC_PROFILE);
        });

        if (!hasAccess) {
          setHasPermission(false);
          setLoading(false);
          return;
        }

        setHasPermission(true);

        // Fetch the organization user data into Redux
        await fetchUser(Number(orgId));
      } catch (error) {
        handleCatched(props, error);
        setHasPermission(false);
      } finally {
        setLoading(false);
      }
    };

    checkPermissionAndFetchOrg();
  }, [orgId, authUser.id]);

  if (loading) {
    return (
      <div className="dashboard-content">
        <CardLoader />
      </div>
    );
  }

  if (!hasPermission) {
    return <Redirect to="/dashboard" />;
  }

  if (!organization) {
    return (
      <div className="dashboard-content">
        <div className="alert alert-danger">
          {t('common:errors.not-found')}
        </div>
      </div>
    );
  }

  // Render OrganizationCommunity with the organization data
  // admin=true to indicate this is an admin managing another org's profile
  return (
    <div className="dashboard-content mb-2">
      <OrganizationCommunity
        {...props}
        publicProfile={organization.public_profile}
        organization={organization}
        admin
      />
    </div>
  );
}

ManagedOrganizationProfile.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      orgId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default withRouter(ManagedOrganizationProfile);
